import { orderBy as kendoOrderBy } from '@progress/kendo-data-query'
import { FiltersComponent } from 'components/filters'
import { LibraryIcon } from 'components/icons/library-icon'
import { SkeletonFilterSidebar, SkeletonSearchBar, SkeletonSourceGrid } from 'components/loading'
import { MobileFilterToggle } from 'components/mobile-filter-toggle'
import { Pagination } from 'components/pagination'
import { RasaPageComponent } from 'components/rasa-page-component'
import { RasaPageComponentProps } from 'components/rasa-page-component/types'
import { SearchBarComponent } from 'components/search-bar'
import { SourceGrid } from 'components/source-grid'
import { Dataset } from 'generic/dataset'
import { shouldShowBackButton } from 'generic/utility'
import { orderBy as lodashOrderBy } from 'lodash'
import React from 'react'
import {
  CLEAR_SEARCH_TERM,
  CLEAR_SELECTED_CATEGORIES,
  GAEvents,
  Pages,
  REVIEW_CLICK,
  SEARCH_PAGE,
} from '../../rootConstants'
import { BackButtonComponent } from '../back-button'
import * as CommonConstants from '../constants'
import { RSSFeedPreview } from '../rss-feed-preview'
import * as GlobalTypes from '../types'
import { sendGAEvent } from '../utils'
import './_styles.scss'
import * as Constants from './constants'
import './modern-styles.scss'
import * as Types from './types'

export class ModernContentLibraryPageComponent extends RasaPageComponent<
  RasaPageComponentProps,
  Types.ContentLibraryPageComponentState
> {
  constructor(props) {
    super(props)
    const store = this.props.store.getState()

    this.state = {
      categories: [],
      isDesktop: store.config.isDesktop,
      isLoading: true,
      maxSourcesReached: false,
      searchTerm: store.contentLibrary.searchTerm || '',
      selectedSources: [],
      selectedCategories: [],
      selectedSubCategories: [],
      categoryFilterMode: 'OR',
      senderPage: store.contentLibrary.senderPage || '',
      skip: 0,
      sort: [],
      sources: [],
      sourcesInfo: store.externalConfig.sourcesInfo,
      subCategories: [],
      subCategoriesMapping: {},
      take: 24, // Changed to 24 for better grid layout (4x6, 3x8, 2x12)
      rssPreviewSource: null,
      isRSSPreviewOpen: false,
      currentPage: 1,
      isMobileFiltersOpen: false,
    }
  }

  public componentDidMount = () => {
    Promise.all([this.getSources(), this.getCategories(), this.getSubCategoriesMapping()]).then(() => {
      this.initialize()
    })
  }

  public render() {
    return (
      <div className="modern-content-library-page">
        {shouldShowBackButton() && (
          <div className="modern-content-library-page__back">
            <BackButtonComponent onClick={this.goBack} />
          </div>
        )}

        <div className="modern-content-library-page__header">
          <div className="modern-content-library-page__header-icon">
            <LibraryIcon />
          </div>
          <div className="modern-content-library-page__header-content">
            <h1 className="modern-content-library-page__title">Content library</h1>
            <p className="modern-content-library-page__description">{Constants.CONTENT_LIBRARY_HEADER_DESCRIPTION}</p>
          </div>
        </div>

        {this.state.isLoading ? (
          <div className="modern-content-library-page__body">
            <div className="modern-content-library-page__sidebar">
              <SkeletonFilterSidebar />
            </div>
            <div className="modern-content-library-page__main">
              <SkeletonSearchBar />
              <SkeletonSourceGrid count={9} />
            </div>
          </div>
        ) : (
          <div className="modern-content-library-page__body">
            {/* Mobile Filter Toggle */}
            <div className="modern-content-library-page__mobile-filter-toggle">
              <MobileFilterToggle
                isOpen={this.state.isMobileFiltersOpen}
                onToggle={this.toggleMobileFilters}
                activeFiltersCount={this.getActiveFiltersCount()}
                totalResultsCount={this.getTotalFilteredCount()}
              />
            </div>

            {/* Sidebar - Hidden on mobile unless toggled */}
            <div className={`modern-content-library-page__sidebar ${
              this.state.isMobileFiltersOpen ? 'modern-content-library-page__sidebar--mobile-open' : ''
            }`}>
              <FiltersComponent
                categories={this.state.categories}
                subCategories={this.state.subCategories}
                subCategoriesMapping={this.state.subCategoriesMapping}
                sources={this.dataToDisplay()}
                fullSources={this.state.sources}
                selectedCategoryName={this.state.selectedCategories[0]}
                selectedCategoryNames={this.state.selectedCategories}
                categoryFilterMode={this.state.categoryFilterMode}
                onCategoryChanged={this.onCategoryChanged}
                onSubCategoryChanged={this.onSubCategoryChanged}
                onCategoryFilterModeChanged={this.onCategoryFilterModeChanged}
                onClearAll={this.clearSelectedCategories}
                searchTerm={this.state.searchTerm}
              />
            </div>

            <div className="modern-content-library-page__main">
              <div className="modern-content-library-page__search">
                <SearchBarComponent onSubmit={this.searchSubmit} />
              </div>

              <div className="modern-content-library-page__notifications">
                <SearchTermNotification onClose={this.clearSearchTerm} searchTerm={this.state.searchTerm} />
                {this.state.maxSourcesReached && (
                  <div className="modern-content-library-page__limit-notice">
                    <span className="modern-content-library-page__limit-notice-icon">⚠️</span>
                    You've reached your limit of <strong>{this.state.sourcesInfo.maxAllowedSources} sources.</strong>
                    Upgrade to unlock more
                  </div>
                )}
              </div>

              <div className="modern-content-library-page__content">
                <div className="modern-content-library-page__content-header">
                  <div className="modern-content-library-page__results-info">
                    {(() => {
                      const totalFiltered = this.getTotalFilteredCount()
                      const currentPageData = this.dataToDisplay()
                      const startItem = (this.state.currentPage - 1) * this.state.take + 1
                      const endItem = Math.min(startItem + currentPageData.length - 1, totalFiltered)
                      
                      return totalFiltered > 0 
                        ? `Showing ${startItem.toLocaleString()} to ${endItem.toLocaleString()} of ${totalFiltered.toLocaleString()} sources`
                        : `No sources found`
                    })()}
                  </div>

                  {this.state.selectedSources.length > 0 && (
                    <div className="modern-content-library-page__selection-info">
                      <span className="modern-content-library-page__selection-count">
                        {this.state.selectedSources.length} selected
                      </span>
                      <button className="modern-content-library-page__review-btn" onClick={this.reviewClick}>
                        Review Selections ({this.state.selectedSources.length})
                      </button>
                      <button
                        className="modern-content-library-page__clear-btn"
                        onClick={this.onClearSourcesSelections}
                      >
                        Clear All
                      </button>
                    </div>
                  )}
                </div>

                <SourceGrid
                  sources={this.dataToDisplay()}
                  selectedSources={this.state.selectedSources}
                  onSelectSource={this.onSelectSource}
                  onPreviewSource={this.onPreviewSource}
                  searchTerm={this.state.searchTerm}
                  maxSourcesReached={this.state.maxSourcesReached}
                  showMetrics={false}
                />
                
                {this.getTotalFilteredCount() > this.state.take && (
                  <Pagination
                    currentPage={this.state.currentPage}
                    totalItems={this.getTotalFilteredCount()}
                    itemsPerPage={this.state.take}
                    onPageChange={this.handlePageChange}
                    showInfo={false}
                    showQuickJump={this.getTotalFilteredCount() > 240} // Show quick jump if more than 10 pages
                    maxVisiblePages={7}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* RSS Feed Preview Modal */}
        <RSSFeedPreview
          source={this.state.rssPreviewSource}
          isOpen={this.state.isRSSPreviewOpen}
          onClose={this.closeRSSPreview}
        />
      </div>
    )
  }

  private onPreviewSource = (source: GlobalTypes.Source): void => {
    if (source.type === GlobalTypes.SourceTypes.rss) {
      this.setState({
        rssPreviewSource: source,
        isRSSPreviewOpen: true,
      })
      sendGAEvent(this.props.store.getState(), {
        action: GAEvents.Page3RSSPreview,
        label: source.name,
      })
    }
  }

  private closeRSSPreview = (): void => {
    this.setState({
      rssPreviewSource: null,
      isRSSPreviewOpen: false,
    })
  }

  private onCategoryFilterModeChanged = (mode: 'OR' | 'AND'): void => {
    this.setState({ categoryFilterMode: mode })
    sendGAEvent(this.props.store.getState(), {
      action: GAEvents.Page3CategoryFilterMode,
      label: mode,
    })
  }

  private searchSubmit = (term: string) => {
    this.setState({ searchTerm: term })
    this.resetPagination()
    sendGAEvent(this.props.store.getState(), { action: GAEvents.Page3Search, label: term })
  }

  private getSources = () => {
    return new Dataset().loadPublicDataset('sources', null).then(response => {
      this.setState({
        sources: response[0]
          .filter(s => !!s.is_content_guide_visible)
          .map(s => {
            s.name = s.name.trim()
            return s
          }),
      })
    })
  }

  private getSubCategoriesMapping = () => {
    return new Dataset().loadPublicDataset('subCategoriesMapping', null).then(response => {
      const mapping = {}
      const subCategories = []
      response[0].forEach((row: any) => {
        mapping[row.sub_category_id] = row
        subCategories.push({
          parent_id: row.category_id,
          name: row.sub_category_name,
          id: row.sub_category_id,
        })
      })
      this.setState({
        subCategories,
        subCategoriesMapping: mapping,
      })
    })
  }

  private getCategories = () => {
    return new Dataset().loadPublicDataset('categories', null).then(response => {
      const orderedCategories = lodashOrderBy(response[0], ['name'], 'asc')
      this.setState({
        categories: orderedCategories,
      })
    })
  }

  private initialize = () => {
    const externalSelectedCategory: string = this.props.store.getState().externalConfig.selectedCategory
    const {
      searchTerm,
      selectedCategory,
      selectedCategories: reduxSelectedCategories,
      selectedSources,
    } = this.props.store.getState().contentLibrary

    // Priority: external config > multiple categories from redux > single category from redux
    const selectedCategories = externalSelectedCategory
      ? [externalSelectedCategory]
      : reduxSelectedCategories && reduxSelectedCategories.length > 0
        ? reduxSelectedCategories
        : selectedCategory && selectedCategory.hasOwnProperty('name')
          ? [selectedCategory.name]
          : []

    this.setState({
      isLoading: false,
      maxSourcesReached: this.maxSourcesReached(selectedSources),
      searchTerm,
      selectedCategories,
      selectedSources,
    })
  }

  // Get all filtered data (not paginated)
  private getFilteredData = (): GlobalTypes.Source[] => {
    const filteredData: GlobalTypes.Source[] = this.state.selectedCategories.length
      ? this.state.sources
          .filter(s => {
            if (s.category) {
              const categoryArray = s.category.split(CommonConstants.CATEGORY_SPLIT_CHAR)

              if (this.state.categoryFilterMode === 'OR') {
                // OR logic: source matches if it has ANY of the selected categories
                for (const category of categoryArray) {
                  if (this.state.selectedCategories.includes(category)) {
                    return true
                  }
                }
                return false
              } else {
                // AND logic: source matches if it has ALL selected categories
                return this.state.selectedCategories.every(selectedCategory => categoryArray.includes(selectedCategory))
              }
            }
            return false
          })
          .filter(s => {
            if (this.state.selectedSubCategories.length) {
              if (s.sub_category) {
                const subCategoryArray = s.sub_category.split(CommonConstants.CATEGORY_SPLIT_CHAR)
                for (const subCategory of subCategoryArray) {
                  if (this.state.selectedSubCategories.includes(subCategory)) {
                    return true
                  }
                }
              }
              return false
            } else {
              return true
            }
          })
      : this.state.sources

    const searchTerm = this.state.searchTerm.toLowerCase()

    const searchedFilteredData = searchTerm
      ? filteredData.filter(
          (s: any) =>
            (s.category || '').toLowerCase().includes(searchTerm) ||
            (s.sub_category || '').toLowerCase().includes(searchTerm) ||
            (s.name || '').toLowerCase().includes(searchTerm) ||
            (s.identifier || '').toLowerCase().includes(searchTerm)
        )
      : filteredData

    return kendoOrderBy(searchedFilteredData, this.state.sort)
  }

  // Get paginated data for display
  private dataToDisplay = (): GlobalTypes.Source[] => {
    const filteredData = this.getFilteredData()
    const startIndex = (this.state.currentPage - 1) * this.state.take
    const endIndex = startIndex + this.state.take
    return filteredData.slice(startIndex, endIndex)
  }

  // Get total count of filtered data
  private getTotalFilteredCount = (): number => {
    return this.getFilteredData().length
  }

  private onCategoryChanged = (categoryName: string, subCategories: []): void => {
    const newSelectedCategories = this.state.selectedCategories.includes(categoryName)
      ? this.state.selectedCategories.filter(c => c !== categoryName)
      : [...this.state.selectedCategories].concat([categoryName])

    if (newSelectedCategories.length > this.state.selectedCategories.length) {
      sendGAEvent(this.props.store.getState(), {
        action: GAEvents.Page3CategorySelect,
        label: categoryName,
      })
    }

    const newSelectedSubCategories = newSelectedCategories.length
      ? subCategories.map((subCategory: GlobalTypes.LightCategory) => subCategory.name)
      : []
    this.setState({
      selectedCategories: newSelectedCategories,
      selectedSubCategories: newSelectedSubCategories,
      // Close mobile filters on small screens after selection for better UX
      isMobileFiltersOpen: window.innerWidth >= 1024 ? this.state.isMobileFiltersOpen : false,
    })
    this.resetPagination()
  }

  private onSubCategoryChanged = (subCategoryName: string): void => {
    const newSelectedSubCategories = this.state.selectedSubCategories.includes(subCategoryName)
      ? this.state.selectedSubCategories.filter(c => c !== subCategoryName)
      : [...this.state.selectedSubCategories].concat([subCategoryName])

    if (newSelectedSubCategories.length > this.state.selectedSubCategories.length) {
      sendGAEvent(this.props.store.getState(), {
        action: GAEvents.Page3SubCategorySelect,
        label: subCategoryName,
      })
    }
    this.setState({ 
      selectedSubCategories: newSelectedSubCategories,
      // Close mobile filters on small screens after selection for better UX
      isMobileFiltersOpen: window.innerWidth >= 1024 ? this.state.isMobileFiltersOpen : false,
    })
    this.resetPagination()
  }

  private onSelectSource = (source: GlobalTypes.Source) => {
    const alreadySelected = this.state.selectedSources.some(s => s.id === source.id)
    const newSelectedSources = alreadySelected
      ? this.state.selectedSources.filter(s => s.id !== source.id)
      : [...this.state.selectedSources].concat([source])
    if (!this.state.maxSourcesReached) {
      if (this.maxSourcesReached(newSelectedSources)) {
        this.setState({ maxSourcesReached: true })
      }
      this.setState({ selectedSources: newSelectedSources })
    } else {
      if (!this.maxSourcesReached(newSelectedSources)) {
        this.setState({ maxSourcesReached: false })
        this.setState({ selectedSources: newSelectedSources })
      }
    }
  }

  private maxSourcesReached = (selectedSources: GlobalTypes.Source[]): boolean => {
    const { activeSourcesCount, maxAllowedSources } = this.state.sourcesInfo
    if (activeSourcesCount && maxAllowedSources) {
      return selectedSources.length + activeSourcesCount >= maxAllowedSources
    } else {
      return false
    }
  }

  private onClearSourcesSelections = (): void => {
    const { activeSourcesCount, maxAllowedSources } = this.state.sourcesInfo
    this.setState({
      maxSourcesReached: activeSourcesCount >= maxAllowedSources,
      selectedSources: [],
    })
  }

  private clearSearchTerm = (): void => {
    this.setState({ searchTerm: '' })
    this.resetPagination()
    this.props.store.dispatch(
      (x => ({ type: CLEAR_SEARCH_TERM, payload: x }))({
        searchTerm: '',
      })
    )
  }

  private clearSelectedCategories = (): void => {
    this.setState({ selectedCategories: [], selectedSubCategories: [] })
    this.resetPagination()
    this.props.store.dispatch(
      (x => ({ type: CLEAR_SELECTED_CATEGORIES, payload: x }))({
        selectedCategory: {},
      })
    )
  }

  private reviewClick = () => {
    this.props.store.dispatch(
      (c => ({ type: REVIEW_CLICK, payload: c }))({
        selectedSources: this.state.selectedSources,
      })
    )
    this.props.navigate(Pages.reviewSelection)
  }

  private goBack = (): void => {
    this.clearSelectedCategories()
    if (this.state.senderPage === SEARCH_PAGE) {
      this.props.navigate(Pages.search)
    } else {
      this.props.navigate(Pages.selectCategory)
    }
  }

  private handlePageChange = (page: number): void => {
    this.setState({ currentPage: page })
    // Scroll to top of results when page changes
    const contentElement = document.querySelector('.modern-content-library-page__content')
    if (contentElement) {
      contentElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Reset pagination when filters change
  private resetPagination = (): void => {
    this.setState({ currentPage: 1 })
  }

  private toggleMobileFilters = (): void => {
    this.setState(prevState => ({
      isMobileFiltersOpen: !prevState.isMobileFiltersOpen
    }))
  }

  // Count active filters for mobile display
  private getActiveFiltersCount = (): number => {
    let count = 0
    if (this.state.searchTerm) count++
    if (this.state.selectedCategories.length > 0) count += this.state.selectedCategories.length
    if (this.state.selectedSubCategories.length > 0) count += this.state.selectedSubCategories.length
    return count
  }
}

const SearchTermNotification = ({ onClose, searchTerm }) => {
  return searchTerm ? (
    <div className="modern-content-library-page__search-notification">
      <div className="modern-content-library-page__search-notification-content">
        <span className="modern-content-library-page__search-notification-text">
          You've searched for '{searchTerm}'
        </span>
        <button
          className="modern-content-library-page__search-notification-close"
          onClick={() => onClose()}
          aria-label="Clear search"
        >
          ×
        </button>
      </div>
    </div>
  ) : null
}
